import React, { useState, useMemo } from "react";
import LayoutHOC from "../LayoutHOC";
import { useParams, Link, useNavigate } from "react-router-dom";
import { DELETE_EMPLOYER, PROFILE_PIC_URL } from "../../utils/constants";
import { useSelector } from "react-redux";
import useEmployerDetails from "../../hooks/useEmployerDetails";
import useEmpJobs from "../../hooks/useEmpJobs";

const EmployerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const details = useEmployerDetails(id);
  const jobs = useEmpJobs(id);
  const user = useSelector((state) => state.user);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [statusFilter, setStatusFilter] = useState("all");

  const profilePicUrl = details?.companylogo;

  const filteredJobs = useMemo(() => {
    let result = jobs?.data || [];
    
    if (searchQuery) {
      result = result.filter(job => 
        (job.title?.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (job.jobPosiiton?.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    if (statusFilter !== "all") {
      result = result.filter(job => {
        if (statusFilter === "published") return job.status === "published";
        if (statusFilter === "expired") return job.status === "expired";
        if (statusFilter === "pending") return job.admin_status === "pending";
        return true;
      });
    }
    
    return result;
  }, [jobs?.data, searchQuery, statusFilter]);

  const paginatedJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredJobs.slice(startIndex, startIndex + pageSize);
  }, [filteredJobs, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredJobs.length / pageSize);

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this employer?");
    if (confirmDelete) {
      try {
        const { token } = user;
        const response = await fetch(`${DELETE_EMPLOYER}${id}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          alert("Employer deleted successfully!");
          navigate("/employer");
        } else {
          alert("Failed to delete employer. Please try again.");
        }
      } catch (error) {
        console.error("Error deleting employer:", error);
        alert("Failed to delete employer. Please try again.");
      }
    }
  };

  const InfoCard = ({ icon, label, value, iconColor = "text-violet-500" }) => (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-gray-50 ${iconColor}`}>
          <i className={`bi ${icon} text-lg`}></i>
        </div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
          <p className="font-medium text-gray-900 mt-1">{value || 'N/A'}</p>
        </div>
      </div>
    </div>
  );

  const StatusBadge = ({ verified, label, icon }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-2">
        <i className={`bi ${icon || (verified ? 'bi-check-circle-fill' : 'bi-clock')} ${verified ? 'text-emerald-500' : 'text-amber-500'}`}></i>
        <span className="text-gray-700">{label}</span>
      </div>
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        verified 
          ? 'bg-emerald-100 text-emerald-700' 
          : 'bg-amber-100 text-amber-700'
      }`}>
        {verified ? 'Verified' : 'Pending'}
      </span>
    </div>
  );

  const DocumentItem = ({ icon, label, url, status, iconBg = "bg-blue-100", iconColor = "text-blue-500", showLink = true }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <i className={`bi ${icon} ${iconColor}`}></i>
        </div>
        <div>
          <p className="font-medium text-gray-900 text-sm">{label}</p>
          {showLink && (
            url ? (
              <a 
                href={PROFILE_PIC_URL + url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-violet-600 hover:underline"
              >
                View Document
              </a>
            ) : (
              <span className="text-xs text-gray-400">Not uploaded</span>
            )
          )}
        </div>
      </div>
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
        status === 'verified' || status === 'approved'
          ? 'bg-emerald-100 text-emerald-700'
          : 'bg-red-100 text-red-700'
      }`}>
        {status === 'verified' || status === 'approved' ? 'Verified' : 'Not Verified'}
      </span>
    </div>
  );

  const JobCard = ({ job }) => {
    const currentTime = new Date();
    const isExpired = new Date(job.deadline) <= currentTime;
    const displayStatus = isExpired ? 'Expired' : 'Published';

    return (
      <div className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{job.title || job.jobPosiiton}</h4>
            <p className="text-sm text-gray-500 mt-1">{job.jobPosiiton}</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <span className="inline-flex items-center px-2 py-1 bg-violet-50 text-violet-700 text-xs rounded-md">
                <i className="bi bi-currency-rupee mr-1"></i>
                {job.minSalary} - {job.maxSalary}
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                <i className="bi bi-calendar3 mr-1"></i>
                {job.deadline}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              displayStatus === 'Published'
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {displayStatus}
            </span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              job.admin_status === '1'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {job.admin_status === '1' ? 'Approved' : 'Pending Review'}
            </span>
            <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
              <i className="bi bi-people mr-1"></i>
              Total: {job.totalVacancy} | Hired: {job.hired_count} | Left: {job.remaining_vacancies}
            </span>
            <Link
              to={`/job_details/${job.id}`}
              className="text-violet-600 hover:text-violet-800 text-sm font-medium mt-1"
            >
              View Details <i className="bi bi-arrow-right"></i>
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main id="maincontent">
      <div className="p-6 mt-14 min-h-screen bg-gradient-to-br from-violet-50 to-purple-50">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/employer"
              className="p-2 rounded-lg bg-white shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
            >
              <i className="bi bi-arrow-left text-gray-600"></i>
            </Link>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-violet-800 to-purple-600 bg-clip-text text-transparent">
                Employer Profile
              </h1>
              <p className="text-gray-500 text-sm">View company details and job postings</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/edit_employer/${id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors font-medium"
            >
              <i className="bi bi-pencil"></i>
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
            >
              <i className="bi bi-trash"></i>
              Delete
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 h-32"></div>
          <div className="px-6 pb-6">
            <div className="flex flex-col md:flex-row gap-6 -mt-16">
              <div className="w-32 h-32 rounded-2xl bg-white p-1 shadow-lg">
                {details?.companylogo ? (
                  <img src={profilePicUrl} alt="Company Logo" className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <div className="w-full h-full rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                    <i className="bi bi-building text-4xl text-violet-500"></i>
                  </div>
                )}
              </div>
              <div className="flex-1 pt-4 md:pt-16">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{details?.name || 'Unknown'}</h2>
                    <h2 className="text-2xl font-bold text-gray-900">{details?.company_name || 'Unknown Company'}</h2>
                    <p className="text-violet-600 font-medium">{details?.industry_type || 'Industry not specified'}</p>
                    <p className="text-gray-500 text-sm mt-1">{details?.email}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.status == 1
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        details?.status == 1 ? 'bg-emerald-500' : 'bg-gray-400'
                      }`}></span>
                      {details?.status == 1 ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.otp_verified === 'verified' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      <i className={`bi ${details?.otp_verified === 'verified' ? 'bi-phone-fill' : 'bi-phone'} mr-1.5`}></i>
                      OTP {details?.otp_verified === 'verified' ? 'Verified' : 'Pending'}
                    </span>
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                      details?.profile_verified === 'verified' 
                        ? 'bg-violet-100 text-violet-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      <i className={`bi ${details?.profile_verified === 'verified' ? 'bi-patch-check-fill' : 'bi-patch-check'} mr-1.5`}></i>
                      Profile {details?.profile_verified === 'verified' ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-building text-violet-500"></i>
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                <InfoCard icon="bi-diagram-3" label="Industry Type" value={details?.industry_type} />
                <InfoCard icon="bi-people" label="Team Size" value={details?.team_size} />
                <InfoCard icon="bi-calendar3" label="Established" value={details?.establishmentYear} />
                <InfoCard icon="bi-telephone" label="Phone" value={details?.mobile} />
                <InfoCard icon="bi-envelope" label="Email" value={details?.email} />
                <InfoCard icon="bi-geo-alt" label="Location" value={details?.address || 'Not specified'} />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-file-text text-violet-500"></i>
                About Company
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {details?.about || 'No description available.'}
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <i className="bi bi-briefcase text-violet-500"></i>
                  Company Job Postings
                  <span className="ml-2 px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full">
                    {filteredJobs.length}
                  </span>
                </h3>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <i className="bi bi-search text-gray-400"></i>
                  </div>
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm bg-white"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="expired">Expired</option>
                    <option value="pending">Pending</option>
                  </select>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 text-sm bg-white"
                  >
                    <option value={5}>5 per page</option>
                    <option value={10}>10 per page</option>
                    <option value={25}>25 per page</option>
                  </select>
                </div>
              </div>
              
              {paginatedJobs.length > 0 ? (
                <div className="space-y-4">
                  {paginatedJobs.map((job, index) => (
                    <JobCard key={job.id || index} job={job} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="bi bi-briefcase text-2xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-500">
                    {searchQuery || statusFilter !== "all" ? "No jobs match your filters" : "No job postings yet"}
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredJobs.length)} of {filteredJobs.length} jobs
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      <i className="bi bi-chevron-left"></i> Previous
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`w-8 h-8 rounded-lg text-sm font-medium ${
                              currentPage === pageNum
                                ? 'bg-violet-600 text-white'
                                : 'border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-shield-check text-violet-500"></i>
                Verification Status
              </h3>
              <div className="space-y-3">
                <StatusBadge 
                  verified={details?.otp_verified === 'verified'} 
                  label="OTP Verification"
                  icon="bi-phone"
                />
                <StatusBadge 
                  verified={details?.profile_verified === 'verified'} 
                  label="Profile Verification"
                  icon="bi-person-badge"
                />
                <StatusBadge 
                  verified={details?.status == 1} 
                  label="Account Status"
                  icon="bi-toggle-on"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <i className="bi bi-folder2-open text-violet-500"></i>
                Documents
              </h3>
              <div className="space-y-3">
                <DocumentItem 
                  icon="bi-building" 
                  label="Company Logo" 
                  url={details?.companylogo}
                  status={details?.companylogo ? 'verified' : null}
                  iconBg="bg-violet-100"
                  iconColor="text-violet-500"
                />
                <DocumentItem 
                  icon="bi-file-earmark-text" 
                  label="GST Number" 
                  status={details?.gstNumber ? 'verified' : null}
                  iconBg="bg-blue-100"
                  iconColor="text-blue-500"
                  showLink={false}
                />
                <DocumentItem 
                  icon="bi-award" 
                  label="CIN Number" 
                  status={details?.cinNumber ? 'verified' : null}
                  iconBg="bg-amber-100"
                  iconColor="text-amber-600"
                  showLink={false}
                />
                <DocumentItem 
                  icon="bi-credit-card" 
                  label="PAN Number" 
                  status={details?.panCardNumber ? 'verified' : null}
                  iconBg="bg-emerald-100"
                  iconColor="text-emerald-500"
                  showLink={false}
                />
              </div>
            </div>

            <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-white/20 rounded-lg">
                  <i className="bi bi-bar-chart text-xl"></i>
                </div>
                <h3 className="font-semibold">Quick Stats</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{jobs?.data?.length || 0}</p>
                  <p className="text-xs text-white/70">Total Jobs</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold">{details?.team_size || 'N/A'}</p>
                  <p className="text-xs text-white/70">Team Size</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LayoutHOC(EmployerDetails);
